// Приоритет лечения для хилеров (аналог приоритета атаки Глобала, global/priority.js).
//
// При размещении хилера с приоритетом игрок выбирает, КОГО лечить (4 опции).
// Выбор фиксируется на card.healPriority до конца игры. Хилер при лечении
// вызывает getHealTarget(state, healer) — он применяет приоритет.
// Если healPriority не задан (бот / sandbox / старый матч) — дефолт lowest_hp
// (= прежнее поведение resolveTarget('ally_lowest_hp'), без регрессии).

// Карты-хилеры с выбором приоритета (текст «лечит … по приоритету лечения»).
export const HEAL_PRIORITY_CARDS = Object.freeze(new Set([
  'livlife_medtekh_ll1', 'livlife_hirurg_ll7', 'livlife_lekar_ll4',
  'livlife_koordinator_vita', 'livlife_glavny_medik_lazar',
  'global_hirurg_gsec', 'global_medik_yantar',
]));

// Список приоритетов для UI-picker'а (кого лечить).
export const HEAL_PRIORITIES = Object.freeze([
  { id: 'lowest_hp', label_ru: 'Самый раненый', label_en: 'Most wounded',
    hint_ru: 'У кого меньше всего HP', hint_en: 'Lowest current HP' },
  { id: 'strongest', label_ru: 'Самый сильный', label_en: 'Strongest',
    hint_ru: 'У кого больше атаки', hint_en: 'Highest attack' },
  { id: 'healer', label_ru: 'Лекаря', label_en: 'Healer',
    hint_ru: 'Союзника-целителя', hint_en: 'A healer ally' },
  { id: 'random', label_ru: 'Случайный', label_en: 'Random',
    hint_ru: 'Без предпочтений', hint_en: 'No preference' },
]);

const VALID = new Set(HEAL_PRIORITIES.map((p) => p.id));
export function isValidHealPriority(id) {
  return typeof id === 'string' && VALID.has(id);
}

// Нужен ли picker приоритета лечения при размещении этой карты.
export function needsHealPriorityPicker(card) {
  if (!card) return false;
  if (!HEAL_PRIORITY_CARDS.has(card.id)) return false;
  return !card.healPriority;
}

export function setHealPriority(card, priority) {
  if (!card) return;
  card.healPriority = isValidHealPriority(priority) ? priority : 'lowest_hp';
}

function readStat(card, key) {
  const cur = card?.current?.[key];
  if (typeof cur === 'number') return cur;
  const base = card?.base?.[key];
  if (typeof base === 'number') return base;
  return card?.[key] ?? 0;
}
function isHealerAlly(card) {
  if (!card) return false;
  if (HEAL_PRIORITY_CARDS.has(card.id)) return true;
  const at = card.ability_text || {};
  const blob = [at.order, at.trigger, at.active, at.aura, at.deathrattle].filter(Boolean).join(' ').toLowerCase();
  return /\bхил\w*|\bлеч\w*|\bheal\w*/.test(blob);
}
function pickByMetric(arr, metric, max) {
  let best = arr[0], bestVal = metric(best);
  for (let i = 1; i < arr.length; i++) {
    const v = metric(arr[i]);
    if ((max && v > bestVal) || (!max && v < bestVal)) { best = arr[i]; bestVal = v; }
  }
  return best;
}
function rngInt(state, len) {
  return (state?.rng?.int) ? state.rng.int(len) : Math.floor(Math.random() * len);
}

// Главная точка: вернуть союзника для лечения по приоритету хилера.
// Дефолт (нет healPriority) = lowest_hp (без регрессии).
export function getHealTarget(state, healer) {
  const owner = state?.players?.[healer?.ownerId];
  if (!owner?.field) return null;
  const allies = [];
  for (const row of owner.field) for (const u of row || []) {
    if (u && u.isAlive && u.isAlive()) allies.push(u);
  }
  if (allies.length === 0) return null;
  switch (healer.healPriority) {
    case 'strongest':
      return pickByMetric(allies, (c) => readStat(c, 'atk'), true);
    case 'healer': {
      const healers = allies.filter(isHealerAlly);
      return healers.length > 0 ? healers[0] : pickByMetric(allies, (c) => readStat(c, 'hp'), false);
    }
    case 'random':
      return allies[rngInt(state, allies.length)];
    case 'lowest_hp':
    default:
      return pickByMetric(allies, (c) => readStat(c, 'hp'), false);
  }
}
